﻿<template>
  <div class="space-y-2">
    <div class="grid grid-cols-3 gap-2">
      <div class="col-span-3">
        <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">{{
          tt('Matter Property', 'Lastnost Matter')
        }}</label>
        <input
          v-model="localConfig.propertyId"
          type="text"
          :placeholder="tt('Select property...', 'Izberite lastnost...')"
          class="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          @input="updateValue" />
      </div>
      <div>
        <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">{{ tt('Cluster ID', 'ID gruče') }}</label>
        <input
          v-model.number="localConfig.clusterId"
          type="number"
          min="0"
          class="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
          disabled />
      </div>
      <div>
        <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">{{
          tt('Attribute ID', 'ID atributa')
        }}</label>
        <input
          v-model.number="localConfig.attributeId"
          type="number"
          min="0"
          class="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
          disabled />
      </div>
      <div>
        <label class="block text-xs text-gray-600 dark:text-gray-400 mb-1">&nbsp;</label>
        <button
          v-if="integrationId"
          @click="openBrowser"
          type="button"
          class="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center gap-1">
          <span class="material-symbols-outlined text-sm">search</span>
          {{ tt('Browse', 'Prebrskaj') }}
        </button>
      </div>
    </div>

    <div v-if="localConfig.propertyId" class="flex items-center gap-1 mt-1">
      <span class="text-xs text-gray-500 dark:text-gray-400 truncate font-mono">{{ localConfig.propertyId }}</span>
    </div>
    <div v-if="mapping" class="text-xs text-gray-500 dark:text-gray-400">
      {{ tt('Expected', 'Pričakovano') }}: {{ tt('Cluster', 'Gruča') }} {{ mapping.matterClusterId }},
      {{ tt('Attribute', 'Atribut') }} {{ mapping.matterAttributeId }}
    </div>

    <!-- Property browser modal -->
    <div
      v-if="showBrowser"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      @mousedown.self="closeBrowser">
      <div
        class="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div>
            <div class="text-sm font-semibold text-gray-900 dark:text-white">
              {{ tt('Select Matter Property', 'Izberite lastnost Matter') }}
            </div>
            <div class="text-xs text-gray-500 dark:text-gray-400">
              <template v-if="props.matterEndpointId">
                <span
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200">
                  <span class="material-symbols-outlined text-sm">link</span>
                  {{ tt('Scoped to:', 'Omejeno na:') }} {{ scopedEndpointLabel }}
                </span>
              </template>
              <template v-else>
                {{ integrationId ? tt('Integration', 'Integracija') + ' ' + integrationId : '' }}
              </template>
              <span v-if="expectedClusterId">
                • {{ tt('Filtered to cluster', 'Filtrirano na gručo') }} {{ expectedClusterId }}</span
              >
            </div>
          </div>
          <button
            type="button"
            class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            @click="closeBrowser">
            <span class="material-symbols-outlined text-lg">close</span>
          </button>
        </div>

        <div class="p-4 space-y-3">
          <div class="flex items-center gap-2">
            <div class="flex-1">
              <input
                v-model="search"
                type="text"
                :placeholder="
                  tt(
                    'Search by node, endpoint, cluster, attribute, or property ID',
                    'Iskanje po vozlišču, končni točki, gruči, atributu ali ID lastnosti',
                  )
                "
                class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
            </div>
            <button
              type="button"
              class="px-3 py-2 text-xs border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1"
              @click="refresh">
              <span class="material-symbols-outlined text-sm">refresh</span>
              {{ tt('Refresh', 'Osveži') }}
            </button>
          </div>

          <div v-if="error" class="text-xs text-red-600 dark:text-red-400">{{ error }}</div>
          <div v-else-if="loading" class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <span class="material-symbols-outlined animate-spin">progress_activity</span>
            {{ tt('Loading Matter properties...', 'Nalaganje lastnosti Matter...') }}
          </div>
          <div v-else class="space-y-2">
            <div class="text-xs text-gray-500 dark:text-gray-400">
              {{ tt('Showing', 'Prikazanih') }} {{ filteredProperties.length }} {{ tt('of', 'od') }}
              {{ properties.length }} {{ tt('properties', 'lastnosti') }}
            </div>
            <div
              class="divide-y divide-gray-200 dark:divide-gray-800 border border-gray-200 dark:border-gray-800 rounded-lg max-h-[55vh] overflow-y-auto">
              <button
                v-for="prop in filteredProperties"
                :key="prop.propertyId"
                type="button"
                class="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800/70 flex items-start gap-3"
                @click="selectProperty(prop)">
                <div class="text-xs font-semibold text-gray-700 dark:text-gray-200 leading-5">
                  #{{ prop.endpointNumber }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between gap-2">
                    <div class="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {{ prop.nodeLabel || tt('Matter Node', 'Vozlišče Matter') }} • EP{{ prop.endpointNumber }}
                    </div>
                    <div
                      class="text-[10px] uppercase tracking-wide px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                      {{ prop.propertyId }}
                    </div>
                  </div>
                  <div class="text-xs text-gray-600 dark:text-gray-300 mt-1 flex flex-wrap gap-2 items-center">
                    <span
                      class="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200">
                      <span class="font-semibold">{{ tt('Cluster', 'Gruča') }} {{ prop.clusterId }}</span>
                      <span class="text-[11px]">{{ prop.clusterName }}</span>
                    </span>
                    <span
                      class="inline-flex items-center gap-1 px-2 py-1 rounded bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200">
                      <span class="font-semibold">{{ tt('Attribute', 'Atribut') }} {{ prop.attributeId }}</span>
                      <span class="text-[11px]">{{ prop.attributeName }}</span>
                    </span>
                    <span
                      v-if="prop.dataType"
                      class="px-2 py-1 rounded bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                      >{{ prop.dataType }}</span
                    >
                    <span
                      v-if="prop.isWritable"
                      class="px-2 py-1 rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200"
                      >{{ tt('writable', 'za pisanje') }}</span
                    >
                    <span
                      v-else
                      class="px-2 py-1 rounded bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                      >{{ tt('read-only', 'samo za branje') }}</span
                    >
                  </div>
                  <div v-if="prop.propertyName" class="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                    {{ prop.propertyName }}
                  </div>
                </div>
              </button>
              <div
                v-if="filteredProperties.length === 0"
                class="p-4 text-xs text-gray-500 dark:text-gray-400 text-center">
                {{ tt('No properties match your search.', 'Nobena lastnost se ne ujema z iskanjem.') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { apiFetch } from '../services/api';
import type { PropertyMapping } from '../shared/propertyMapping';
import { tt } from '../utils/consoleText';

export interface MatterConfig {
  propertyId: string;
  clusterId: number;
  attributeId: number;
}

interface MatterPropertyItem {
  propertyId: string;
  clusterId: number;
  attributeId: number;
  propertyName?: string;
  dataType?: string;
  isWritable?: boolean;
  nodeLabel?: string;
  nodeId?: string;
  endpointId?: string;
  endpointNumber?: number;
  clusterName?: string;
  attributeName?: string;
}

interface Props {
  modelValue?: MatterConfig;
  mapping: PropertyMapping | undefined;
  integrationId: string;
  /** UUID of device_endpoints.protocol_endpoint_id — constrains browser to this endpoint only */
  matterEndpointId?: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: MatterConfig];
}>();

const localConfig = ref<MatterConfig>({
  propertyId: props.modelValue?.propertyId || '',
  clusterId: props.modelValue?.clusterId ?? props.mapping?.matterClusterId ?? 0,
  attributeId: props.modelValue?.attributeId ?? props.mapping?.matterAttributeId ?? 0,
});

const showBrowser = ref(false);
const loading = ref(false);
const error = ref<string | null>(null);
const search = ref('');
const properties = ref<MatterPropertyItem[]>([]);

const expectedClusterId = computed(() => props.mapping?.matterClusterId);

/**
 * The device class mapping carries the cluster id the way the class writes it — a
 * hexadecimal string (`'0x0008'`, see `class-matter_light_color_temp.json`) — while the
 * hub's payload carries a number (`8`). Compare numerically: `parseInt('0x0008', 16)` is
 * `8`, `Number()` for any other spelling, `undefined` when the mapping has none.
 */
function clusterIdOf(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = /^0x[0-9a-f]+$/i.test(trimmed) ? parseInt(trimmed, 16) : Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

const filteredProperties = computed(() => {
  let list = properties.value;
  // When device is endpoint-linked, restrict to that endpoint's properties only
  if (props.matterEndpointId) {
    list = list.filter((p) => p.endpointId === props.matterEndpointId);
  }
  const wantedCluster = clusterIdOf(props.mapping?.matterClusterId);
  if (wantedCluster !== undefined) {
    list = list.filter((p) => Number(p.clusterId) === wantedCluster);
  }
  if (!search.value) return list;
  const term = search.value.toLowerCase();
  return list.filter(
    (p) =>
      p.propertyId.toLowerCase().includes(term) ||
      (p.nodeLabel || '').toLowerCase().includes(term) ||
      String(p.endpointNumber ?? '').includes(term) ||
      String(p.clusterId).includes(term) ||
      String(p.attributeId).includes(term) ||
      (p.clusterName || '').toLowerCase().includes(term) ||
      (p.attributeName || '').toLowerCase().includes(term) ||
      (p.propertyName || '').toLowerCase().includes(term),
  );
});

// Label for the scoped endpoint (first match from loaded properties)
const scopedEndpointLabel = computed(() => {
  if (!props.matterEndpointId) return null;
  const match = properties.value.find((p) => p.endpointId === props.matterEndpointId);
  if (!match) return `${tt('Endpoint', 'Končna točka')} ${props.matterEndpointId.slice(0, 8)}…`;
  return `${match.nodeLabel || tt('Matter Node', 'Vozlišče Matter')} · EP${match.endpointNumber}`;
});

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) {
      localConfig.value = { ...newVal };
    }
  },
  { deep: true },
);

function updateValue() {
  emit('update:modelValue', { ...localConfig.value });
}

async function openBrowser() {
  showBrowser.value = true;
  if (properties.value.length === 0) {
    await loadProperties();
  }
}

function closeBrowser() {
  showBrowser.value = false;
}

async function loadProperties() {
  loading.value = true;
  error.value = null;
  try {
    const nodes = await apiFetch(`/integrations/${props.integrationId}/matter-nodes`);
    const collected: MatterPropertyItem[] = [];
    for (const node of Array.isArray(nodes) ? nodes : []) {
      const endpoints = node?.endpoints || [];
      for (const ep of endpoints) {
        const endpointProperties = ep?.properties || [];
        for (const prop of endpointProperties) {
          // The hub answers camelCase — nodeLabel, endpointId, endpointNumber,
          // clusterName, attributeName, isWritable (only node_id is snake_case, on the
          // node). MatterNodesTab.vue reads the same answer the same way.
          collected.push({
            propertyId: prop.propertyId,
            clusterId: prop.clusterId,
            attributeId: prop.attributeId,
            propertyName: prop.propertyName,
            dataType: prop.dataType,
            isWritable: prop.isWritable,
            nodeLabel: node.nodeLabel,
            nodeId: node.node_id,
            endpointId: ep.endpointId,
            endpointNumber: ep.endpointNumber,
            clusterName: prop.clusterName,
            attributeName: prop.attributeName,
          });
        }
      }
    }
    properties.value = collected;
  } catch (__caught: unknown) {
    const err = __caught as Error;
    console.error('Error loading Matter properties', err);
    error.value = err?.message || tt('Failed to load Matter properties', 'Lastnosti Matter ni bilo mogoče naložiti');
  } finally {
    loading.value = false;
  }
}

async function refresh() {
  properties.value = [];
  await loadProperties();
}

function selectProperty(prop: MatterPropertyItem) {
  localConfig.value = {
    propertyId: prop.propertyId,
    clusterId: prop.clusterId,
    attributeId: prop.attributeId,
  };
  updateValue();
  closeBrowser();
}
</script>
